import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getCodeData,
  getInitFormData,
  getTablebySql,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import { showPopupModal } from "@widgets/CommonModal";
import SN_PUR_RCV_REG_002_POP_001 from "@menu/PUR/SN_PUR_RCV_REG_002/SN_PUR_RCV_REG_002_POP_001";
import { fnCheckAutoProcessV2 } from "../common/sn-pur-util";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";
import SnResizeBar from "@shared/components/SnResizeBar";
import SN_PUR_RCV_REG_002_POP_002 from "./SN_PUR_RCV_REG_002_POP_002";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

const SN_PUR_RCV_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const calmRef = useRef<any>(null);
  const ioType = useRef<any>(null);
  let approveCellCh: Boolean = true;
  const today = dateToString(new Date());
  const [selectedDocu, setSelectedDocu] = useState<string>(""); // file 관련

  useEffect(() => {
    const fetchform = async () => {
      const masVal = config?.MAS0002?.dc_ctrl ?? "";
      const purVal = config?.PUR0105?.dc_ctrl ?? "";
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);

        if (masVal === "N") {
          const grid2 = rtn.grid.grid2.map((col: any) =>
            ["cd_pjt", "no_wbs", "no_cbs"].includes(col.cd_column)
              ? { ...col, fg_visible: "N" }
              : col
          );
          setColums({
            grid1: rtn.grid.grid1,
            grid2: grid2,
          });
        } else if (masVal === "Y" && purVal === "Y") {
          const grid2 = rtn.grid.grid2.map((col: any) =>
            ["cd_pjt"].includes(col.cd_column)
              ? { ...col, yn_required: "Y" }
              : col
          );
          setColums({
            grid1: rtn.grid.grid1,
            grid2: grid2,
          });
        } else {
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
          });
        }
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    const fetchCalm = async () => {
      if (!calmRef.current) {
        // 최초 1번만 조회
        const SYS014 = {
          sql: "select cd_flag, dc_ref from mas_codel where cd_firm = :cd_firm and cd_clas = 'SYS014'",
          cd_firm: userInfo.cd_firm,
        };
        calmRef.current = await getTablebySql(SYS014);
      }
    };
    fetchCalm();
  }, [userInfo.cd_firm]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_pur_rcv_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const modifiedRowsH =
        gridRefs.current["grid1"].current.getChange().insert;
      if (modifiedRowsH.length > 0) return;

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_biz_rcv: userInfo.cd_biz,
        cd_user_reg: userInfo.cd_user,
        fg_io: "1",
        cd_io: "",
        no_req: "",
        cd_dept: userInfo.cd_dept,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_req: today,
        cd_exch: "000",
        rt_exch: 1,
        fg_money: "1",
        fg_trns: searchInfo.fg_trns ?? "1",
        cd_gp: userInfo.cd_gp,
        st_req: "F",
        yn_autorcv: "Y",
        yn_autoprocess: "N",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;
      return gridRefs.current["grid2"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowL = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");

      return {
        cd_firm: userInfo.cd_firm,
        no_line: maxNoLine + 1,
        no_req: focusedNodeH.data?.no_req,
        no_line_rcv: 0,
        no_line_rcv_src: 0,
        dt_dlv: dateToString(new Date()),
        st_req: "F",
        cd_wh: "",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;

    gridRefs.current["grid2"].current?.deleteSelectedRows();

    setFooterDataToRow();
  };

  const onSave = async () => {
    let purRcvReqHChanges;
    let purRcvReqLChanges;
    const mtlQtoH: any[] = [];
    const mtlQtoL: any[] = [];
    const mtlQtoUH: any[] = [];
    const mtlQtoUL: any[] = [];
    const mtlQtoDH: any[] = [];
    const mtlQtoDL: any[] = [];
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return false;

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      purRcvReqHChanges = gridRefs.current["grid1"].current.getChange();
      purRcvReqLChanges = gridRefs.current["grid2"].current.getChange();

      const headerInserts = purRcvReqHChanges.insert ?? [];
      const lineInserts = purRcvReqLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return false;
      }

      if (
        purRcvReqHChanges.modifiedRowcount === 0 &&
        purRcvReqLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      for (let row of headerInserts) {
        if (!row.no_req) {
          const cd_module: any = Menuprops.cd_module;
          const cd_menu: any = Menuprops.cd_menu;
          const noSlip = await applySlip({
            cd_column: "no_req",
            cd_module: cd_module,
            no_line: 4,
            cd_prefix: "",
            dt: row.dt_req,
            girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
            inserts: [purRcvReqHChanges, purRcvReqLChanges],
            cd_menu: cd_menu,
          });
          gridRefs.current["grid2"].current.applyFilters({ no_req: noSlip });

          if (noSlip) {
            row.no_req = noSlip;
            const qtioH = {
              cd_firm: userInfo.cd_firm,
              no_io: noSlip,
              cd_biz: row?.cd_biz,
              dt_io: row.dt_req,
              cd_cust: row?.cd_cust,
              cd_cust_dlv: row?.cd_cust_dlv ?? null,
              cd_exch: row?.cd_exch,
              rt_exch: row?.rt_exch ?? 0,
              fg_trns: row?.fg_trns,
              cd_io: row?.cd_io ?? "100",
              yn_autoprocess: "Y",
              fg_map: "1",
              am: row?.am,
              am_k: row?.am_k,
              am_vat: row?.am_vat,
              am_supply: row?.am_supply,
              cd_wh: row?.cd_wh,
              cd_emp: row?.cd_emp ?? userInfo.cd_emp,
              cd_dept: userInfo.cd_dept,
              cd_gp: row?.cd_gp,
              cd_mgmtdtl: row?.cd_mgmtdtl ?? null,
              fg_slip: row?.fg_slip ?? "1",
              tp_slip: row?.tp_slip ?? "1",
              fg_io: row?.fg_io ?? "1",
              fg_money: row?.fg_money,
              dc_rmk: row?.dc_rmk,
              no_hst: 0,
            };
            mtlQtoH.push(qtioH);
            for (let rowL of lineInserts) {
              if (!rowL.no_req) {
                if (noSlip) {
                  rowL.no_req = noSlip;
                }
              }
              const qtioL = {
                cd_firm: userInfo.cd_firm,
                no_io: noSlip,
                cd_item: rowL?.cd_item,
                cd_wh: rowL?.cd_wh,
                cd_biz: row?.cd_biz,
                cd_io: row?.cd_io ?? "100",
                fg_io: ioType.current?.fg_io ?? "1",
                fg_slip: ioType.current?.fg_slip ?? "1",
                tp_slip: ioType.current?.tp_slip ?? "1",
                fg_money: ioType.current?.fg_money ?? "1",
                dt_io: dateToString(new Date()),
                qt: rowL?.qt ?? 0,
                qt_good: rowL?.qt_good ?? 0,
                qt_bad: rowL?.qt_bad ?? 0,
                cd_loc: rowL?.cd_loc,
                um: rowL?.um ?? 0,
                um_k: rowL?.um_k ?? 0,
                am: rowL?.am ?? 0,
                am_k: rowL?.am_k ?? 0,
                am_vat: rowL?.am_vat ?? 0,
                um_supply: rowL?.um_supply ?? 0,
                am_supply: rowL?.am_supply ?? 0,
                fg_vat: rowL?.fg_vat,
                no_order: rowL?.no_po,
                no_line_order: rowL?.no_line_po,
                no_lc: rowL?.no_lc,
                no_line_lc: rowL?.no_line_lc,
                no_ip: rowL?.no_ip,
                no_line_ip: rowL?.no_line_ip ?? 0,
                cd_pjt: rowL?.cd_pjt,
                dc_rmk: rowL?.dc_rmk,
                no_req: rowL?.no_req,
                no_line_req: rowL?.no_line,
                no_line: rowL?.no_line,
                no_lot: rowL?.no_lot,
                no_src_lot: "",
                no_line_src_lot: 0,
                no_line_lot_src_lot: 0,
                no_line_src: 0,
                qt_cls: rowL?.qt_cls ?? 0,
                rt_exch: rowL?.rt_exch ?? 0,
                yn_req: "Y",
                fg_map: ioType.current?.fg_map ?? "1",
              };
              mtlQtoL.push(qtioL);
            }
          }
        }
      }

      const headerDeletes = purRcvReqHChanges.delete ?? [];
      const lineDeletes = purRcvReqLChanges.delete ?? [];

      for (let rowDL of lineDeletes) {
        const qtioL = {
          cd_firm: rowDL?.cd_firm,
          no_io: rowDL?.no_req,
          no_line: rowDL?.no_line,
          fg_slip: rowDL?.fg_slip ?? "1",
        };
        mtlQtoDL.push(qtioL);
      }

      for (let rowDH of headerDeletes) {
        const qtioH = {
          cd_firm: userInfo.cd_firm,
          no_io: rowDH?.no_req,
        };
        mtlQtoDH.push(qtioH);
      }

      const saveData = {
        pur_rcvreqh: purRcvReqHChanges,
        pur_rcvreql: purRcvReqLChanges,
      };
      const saveData2: any = {
        mtl_qtioh: {},
        mtl_qtio: {},
      };

      if (mtlQtoH || mtlQtoL) {
        saveData2.mtl_qtioh.insert = mtlQtoH || [];
        saveData2.mtl_qtio.insert = mtlQtoL || [];
      }

      if (mtlQtoUH || mtlQtoUL) {
        saveData2.mtl_qtioh.update = mtlQtoUH || [];
        saveData2.mtl_qtio.update = mtlQtoUL || [];
      }

      if (mtlQtoDH || mtlQtoDL) {
        saveData2.mtl_qtio.delete = mtlQtoDL || [];
        saveData2.mtl_qtioh.delete = mtlQtoDH || [];
      }

      const saveDataAll = {
        ...saveData,
        ...saveData2,
      };
      const ok = await fnSave("SN_PUR_RCV_REG_002_SAVE_01", saveDataAll);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const rowChanges = gridRefs.current["grid1"].current.getChange().insert;
      if (rowChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: rowChanges[0].cd_cust || "",
        nm_cust: rowChanges[0].nm_cust || "",
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        fg_trns: rowChanges[0].fg_trns || "1",
      };

      const component = (
        <SN_PUR_RCV_REG_002_POP_001 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "발주적용",
        children: component,
        width: "72%",
        validation: true,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];

      const cd_po = headerResult[0]?.cd_po;
      const param = await getCodeData(
        "",
        `SELECT mit.cd_io, mit.fg_slip, mit.tp_slip, mit.fg_io, mit.fg_money, mit.fg_map
             FROM mas_potype mpt
             LEFT OUTER JOIN mas_iotype mit ON mpt.cd_firm = mit.cd_firm AND mpt.cd_io = mit.cd_io
             WHERE mpt.cd_firm = '${userInfo.cd_firm}'
             AND mpt.cd_po = '${cd_po}'
             AND mit.cd_module = 'PUR'`,
        false
      );
      if (param != null && param.length > 0) {
        ioType.current = param[0];
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        for (const key in headerResult[0]) {
          focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        }
        focusedRowNodeH.setDataValue(
          "cd_biz_isu",
          headerResult[0].cd_biz,
          "noEvent"
        );
        ////ISS2026060128 요청으로 주석
        // focusedRowNodeH.setDataValue(
        //   "dt_req",
        //   dateToString(new Date()),
        //   "noEvent"
        // );
        focusedRowNodeH.setDataValue(
          "cd_biz_isu",
          headerResult[0].cd_biz,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_io",
          ioType.current?.cd_io || "100",
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "fg_slip",
          ioType.current?.fg_slip || "1",
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "tp_slip",
          ioType.current?.tp_slip || "1",
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "fg_io",
          ioType.current?.fg_io || "1",
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "fg_money",
          ioType.current?.fg_money || "1"
        );
        focusedRowNodeH.setDataValue(
          "fg_map",
          ioType.current?.fg_map || "1",
          "noEvent"
        );
        focusedRowNodeH.setDataValue("yn_autorcv", "Y", "noEvent");
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              if (key === "no_line") continue;
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("no_line_po", result.no_line, "noEvent");
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
          }
        }
      });

      CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
      gridRefs.current["grid2"].current?.clearFocus();

      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      const lastRowIndex = rowCount - 1;
      gridRefs.current["grid2"].current.selectAndFocusRow(lastRowIndex);
    }

    if (id == "btnLCApply") {
      const rowChanges = gridRefs.current["grid1"].current.getChange().insert;
      if (rowChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: selectedFormData.cd_cust,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        fg_trns: selectedFormData.fg_trns,
      };

      const component = (
        <SN_PUR_RCV_REG_002_POP_002 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "L/C적용",
        children: component,
        width: "72%",
        // validation: true,
      });
      if (!result || result === "close") return;

      const headerResult = (result[0]?.length ? result[0] : result[2]) ?? [];
      const lineResult: any[] =
        (result[1]?.length ? result[1] : result[3]) ?? [];

      const cd_io = headerResult[0]?.cd_io;
      const param = await getCodeData(
        "",
        `select cd_io, fg_slip, tp_slip, fg_io, fg_money, fg_map
             from mas_iotype
             where cd_firm = '${userInfo.cd_firm}'
               and cd_io = '${cd_io}'
               and cd_module = 'PUR'`,
        false
      );
      if (param != null && param.length > 0) {
        ioType.current = param[0];
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();

      if (focusedRowNodeH && headerResult[0]) {
        for (const key in headerResult[0]) {
          focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        }
        //focusedRowNodeH.setDataValue("dt_req", today, "noEvent"); //ISS2026060128 요청으로 주석
        focusedRowNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0]["nm_cust"],
          "noEvent"
        );
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              if (key === "no_line") continue;
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("no_line_lc", result.no_line_lc, "noEvent");
            addedNode.setDataValue("no_line_po", result.no_line, "noEvent");
            addedNode.setDataValue("qt", result.qt, "noEvent");
          }
        }
      });

      CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const no_req = selectedRow.no_req;
      setSelectedDocu(no_req);
    } else {
      return;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "fg_trns") {
      const fg_trns = rowData?.fg_trns ?? "1";
      let fg_vat = "21"; // 기본값
      if (fg_trns === "1") {
        fg_vat = "21";
      } else if (fg_trns === "2" || fg_trns === "3") {
        fg_vat = "23";
      } else if (fg_trns === "4" || fg_trns === "5") {
        fg_vat = "56";
      }
      rowNode.setDataValue("fg_vat", fg_vat);
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("fg_trns", fg_trns, "noEvent");
        node.setDataValue("fg_vat", fg_vat, "noEvent");
      });
    }
    CalAmBulk(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChangedL = async (event: CellValueChangedEvent) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "nm_wh") {
      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      if (rowCount === 1) return;

      if (
        rowData.cd_wh === "" ||
        rowData.cd_wh === null ||
        rowData.cd_wh === undefined
      )
        return;

      let apply = false;
      gridRefs.current["grid2"].current?.forEachFilteredNode(
        (node: RowNode) => {
          if (
            node.data.cd_wh === "" ||
            node.data.cd_wh === null ||
            node.data.cd_wh === undefined
          )
            apply = true;
        }
      );
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );
      if (confirm) {
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          }
        );
      }
    }
    CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const setFooterDataToRow = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRowL,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "구매입고정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
            header={
              <div>
                <SnButton
                  text={"L/C적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnLCApply");
                  }}
                />
                <SnButton
                  text={"발주적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_PUR_RCV_REG_002;
